<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';
import AgenteIaAPI from 'dashboard/api/agenteIa';
import AgenteIaLayout from '../components/AgenteIaLayout.vue';
import { passosDoInicio, faixaDoTeto } from '../helpers';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const status = ref(null);
const carregando = ref(true);
const erro = ref('');

const carregar = async () => {
  carregando.value = true;
  try {
    const { data } = await AgenteIaAPI.status();
    status.value = data;
  } catch (e) {
    erro.value = e?.response?.data?.error || t('AGENTE_IA.INICIO.ERRO');
  } finally {
    carregando.value = false;
  }
};

onMounted(carregar);

const situacao = computed(() => status.value?.vinculo?.status);
const pronto = computed(() => situacao.value === 'ativo');
const passos = computed(() => passosDoInicio(status.value));
const modo = computed(() =>
  status.value?.persona?.mode === 'production' ? 'PRODUCAO' : 'TESTE'
);
const numero = valor => new Intl.NumberFormat().format(valor || 0);
const moeda = valor =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    valor || 0
  );
const faixa = computed(() => faixaDoTeto(status.value?.teto));
// Chave colada em Chaves de IA que o agente ainda nao usa: o aviso manda escolher a chave, nao colar outra.
const avisoSemChave = computed(() =>
  status.value?.persona?.chave_salva ? 'CHAVE_SALVA' : 'SEM_CHAVE'
);
</script>

<template>
  <AgenteIaLayout
    :title="t('AGENTE_IA.INICIO.TITULO')"
    :description="t('AGENTE_IA.INICIO.DESCRICAO')"
  >
    <p v-if="carregando" class="text-sm text-n-slate-11">
      {{ t('AGENTE_IA.CARREGANDO') }}
    </p>
    <p v-else-if="erro" class="text-sm text-n-ruby-11">{{ erro }}</p>

    <template v-else-if="status">
      <div
        v-if="!pronto"
        class="p-4 text-sm rounded-lg bg-n-alpha-black2 text-n-slate-12"
      >
        <p class="font-medium">
          {{ t(`AGENTE_IA.INICIO.SITUACAO.${situacao?.toUpperCase()}`) }}
        </p>
      </div>

      <div
        v-if="pronto && !status.persona?.com_chave"
        class="flex flex-col gap-2 p-4 text-sm rounded-lg bg-n-amber-3 text-n-amber-11 sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="flex flex-col gap-0.5">
          <span class="font-medium">{{
            t(`AGENTE_IA.INICIO.${avisoSemChave}.TITULO`)
          }}</span>
          <span class="text-xs">{{
            t(`AGENTE_IA.INICIO.${avisoSemChave}.TEXTO`)
          }}</span>
        </div>
        <router-link
          :to="{
            name: 'agente_ia_chaves',
            params: { accountId: route.params.accountId },
          }"
          class="text-sm font-medium underline shrink-0"
        >
          {{ t(`AGENTE_IA.INICIO.${avisoSemChave}.ACAO`) }}
        </router-link>
      </div>

      <div v-else class="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-alpha-black2">
          <span class="text-xs text-n-slate-11">
            {{ t('AGENTE_IA.INICIO.MOTOR') }}
          </span>
          <span
            class="text-sm font-medium"
            :class="status.motor?.ok ? 'text-n-teal-11' : 'text-n-ruby-11'"
          >
            {{
              status.motor?.ok
                ? t('AGENTE_IA.INICIO.MOTOR_OK')
                : t('AGENTE_IA.INICIO.MOTOR_FORA')
            }}
          </span>
        </div>
        <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-alpha-black2">
          <span class="text-xs text-n-slate-11">
            {{ status.persona?.name }}
          </span>
          <span class="text-sm font-medium text-n-slate-12">
            {{ t(`AGENTE_IA.MODO.${modo}`) }}
          </span>
        </div>
        <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-alpha-black2">
          <span class="text-xs text-n-slate-11">
            {{ t('AGENTE_IA.INICIO.CAIXAS') }}
          </span>
          <span class="text-sm font-medium text-n-slate-12">
            {{ status.caixas_ligadas }}
          </span>
        </div>
        <div class="flex flex-col gap-1 p-4 rounded-lg bg-n-alpha-black2">
          <span class="text-xs text-n-slate-11">
            {{ t('AGENTE_IA.INICIO.CONSUMO') }}
          </span>
          <span class="text-sm font-medium text-n-slate-12">
            {{
              t('AGENTE_IA.INICIO.TOKENS', {
                entrada: numero(status.consumo_mes.tokens_entrada),
                saida: numero(status.consumo_mes.tokens_saida),
              })
            }}
          </span>
        </div>
      </div>

      <div
        v-if="pronto && status.teto"
        class="flex flex-col gap-1 p-4 text-sm rounded-lg"
        :class="{
          'bg-n-alpha-black2 text-n-slate-12': faixa === 'NORMAL',
          'bg-n-amber-3 text-n-amber-11': faixa === 'AVISO',
          'bg-n-ruby-3 text-n-ruby-11': faixa === 'PAUSADO',
        }"
      >
        <span>
          {{
            t('AGENTE_IA.TETO.USO', {
              gasto: moeda(status.teto.gasto),
              teto: moeda(status.teto.valor),
              percentual: status.teto.percentual,
            })
          }}
        </span>
        <span v-if="faixa !== 'NORMAL'" class="text-xs">
          {{ t(`AGENTE_IA.TETO.${faixa}`) }}
        </span>
      </div>

      <section v-if="pronto" class="flex flex-col items-start gap-2">
        <Button
          :label="t('AGENTE_IA.INICIO.LIGAR_CAIXA')"
          :disabled="!status.persona?.com_chave"
          @click="
            router.push({
              name: 'agente_ia_caixas',
              params: { accountId: route.params.accountId },
            })
          "
        />
        <p v-if="!status.persona?.com_chave" class="text-xs text-n-amber-11">
          {{ t('AGENTE_IA.CAIXAS.SEM_CHAVE') }}
        </p>
      </section>

      <section v-if="pronto" class="flex flex-col gap-3">
        <h2 class="text-sm font-medium text-n-slate-12">
          {{ t('AGENTE_IA.INICIO.PASSOS.TITULO') }}
        </h2>
        <ol class="flex flex-col gap-2">
          <li
            v-for="(passo, indice) in passos"
            :key="passo.chave"
            class="flex items-start gap-3 p-3 rounded-lg border border-n-weak"
          >
            <span
              class="flex items-center justify-center flex-shrink-0 text-xs rounded-full size-6"
              :class="
                passo.feito
                  ? 'bg-n-teal-9 text-white'
                  : 'bg-n-alpha-black2 text-n-slate-11'
              "
            >
              <span v-if="passo.feito" class="i-lucide-check size-3.5" />
              <template v-else>{{ indice + 1 }}</template>
            </span>
            <div class="flex flex-col gap-0.5">
              <router-link
                v-if="
                  passo.rota &&
                  (passo.chave !== 'LIGAR' || status.persona?.com_chave)
                "
                :to="{
                  name: passo.rota,
                  params: { accountId: route.params.accountId },
                }"
                class="text-sm font-medium text-n-brand"
              >
                {{ t(`AGENTE_IA.INICIO.PASSOS.${passo.chave}.TITULO`) }}
              </router-link>
              <span
                v-else
                class="text-sm font-medium"
                :class="passo.feito ? 'text-n-slate-11' : 'text-n-slate-12'"
              >
                {{ t(`AGENTE_IA.INICIO.PASSOS.${passo.chave}.TITULO`) }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ t(`AGENTE_IA.INICIO.PASSOS.${passo.chave}.AJUDA`) }}
              </span>
            </div>
          </li>
        </ol>
      </section>
    </template>
  </AgenteIaLayout>
</template>
